import { Projectile } from '../../shared/CharacterTypes';
import {
  buildRect, buildCircle, buildStar, buildCard, buildArrow,
  buildCrescent, buildSpiral, buildBeam, buildHammer, buildBlade,
  type ProjectileShapeBuilder,
} from './shared/ProjectileShapes';
import { createGraphics } from './utils/Graphics';
import type { IGraphics } from './IRenderer';
import { RenderCountTracker } from '../diagnostics/RenderCountTracker';
import { TrailSystem } from '../effects/players/TrailSystem';
import { Z_LAYERS } from './Z_LAYERS';
import { buildThrowingStar } from './shared/ThrowingStarShape';
import { throwingStarRotation } from '../../config/abilities/throwingStarVisuals.config';
import { isThrowingStarAbility } from '../../shared/combat/throwingStars';
import { DisplayPool, isParkable, type Parkable } from './pool/DisplayPool';
// SHAPE_ASPECT is the canonical natural thickness-to-length ratio per shape —
// it lives next to PROJECTILE_VFX so the size-from-scalar SSOT (AbilityBuilder /
// NovaProjectileBehavior / TomlAbilityFactory) and these SHAPE_BUILDERS share
// one source. Re-exported here so a reader of the renderer sees both halves.
export { SHAPE_ASPECT } from '../../shared/config/projectile-vfx.config';

/**
 * Handles the visual representation of projectiles.
 * Strictly separated from ProjectileSystem (Logic).
 *
 * Phase H-B Stage 5h: each projectile is a single IGraphics handle that
 * holds all its layered draw calls (glow + inner + core + highlight).
 * Position + rotation are mutated each frame from ProjectileSystem state.
 *
 * Spin: physical throwing stars use their launch age; spirals accumulate
 * a spin speed. Other shapes rotate to match velocity direction.
 */
interface ProjectileVisual {
  graphics: IGraphics;
  /** DisplayPool key: builder + size + colours (what the body draws). */
  poolKey: string;
  spinSpeed?: number; // radians/sec
  throwingStar?: boolean;
  /** Last position given to the graphic (render-state truth for QA). */
  drawX?: number;
  drawY?: number;
}

/** Projectile id → where its graphic was last placed (world px). Empty when no renderer is live. */
export function drawnProjectilePositions(): Map<string, { x: number; y: number }> {
  return ProjectileRenderer.live?.drawnPositions() ?? new Map();
}

const SHAPE_BUILDERS: Record<string, ProjectileShapeBuilder> = {
  rect: buildRect,
  circle: buildCircle,
  // TOML vfx.projectileShape names for round projectiles — without these the
  // canonical data values 'orb'/'sphere' (mage_auto, fireball, …) missed the
  // map and fell back to `rect`, so a data-driven projectile rendered as a
  // rectangle. Alias them to the round builder.
  orb: buildCircle,
  sphere: buildCircle,
  star: buildStar,
  card: buildCard,
  arrow: buildArrow,
  crescent: buildCrescent,
  spiral: buildSpiral,
  beam: buildBeam,
  hammer: buildHammer,
  blade: buildBlade,
};

/** Shapes that tumble at a fixed spin instead of facing their velocity (rad/s). */
const SHAPE_SPIN: Readonly<Record<string, number>> = { spiral: 8, hammer: 14 };

export class ProjectileRenderer {
  private scene: unknown;
  private visuals = new Map<string, ProjectileVisual>();
  private lastTickMs = performance.now();
  /**
   * Projectile bodies parked at alpha 0 for the next projectile with the same
   * look (plan 16 §2.2): a Lightning Bolt every 0.7 s no longer adds and
   * removes a Graphics — each of which rebuilt the Pixi stage.
   */
  private pool = new DisplayPool<IGraphics & Parkable>({ perKeyCap: 6, globalCap: 48 });

  /** The live renderer, read by `__h2kRenderState` for where each graphic was drawn. */
  static live: ProjectileRenderer | null = null;

  constructor(scene: unknown) {
    this.scene = scene;
    ProjectileRenderer.live = this;
  }

  public drawnPositions(): Map<string, { x: number; y: number }> {
    const out = new Map<string, { x: number; y: number }>();
    for (const [id, v] of this.visuals) {
      if (v.drawX !== undefined && v.drawY !== undefined) out.set(id, { x: v.drawX, y: v.drawY });
    }
    return out;
  }

  /**
   * Syncs visual state with logic state.
   * Call this every frame after ProjectileSystem.update()
   */
  public update(projectiles: Projectile[]): void {
    const now = performance.now();
    const simulationNow = Date.now();
    const dtSec = Math.min(0.1, (now - this.lastTickMs) / 1000);
    this.lastTickMs = now;

    const activeIds = new Set<string>();

    for (const proj of projectiles) {
      activeIds.add(proj.id);
      RenderCountTracker.note(`proj:${proj.id}`);
      let visual = this.visuals.get(proj.id);

      if (!visual) {
        visual = this.createProjectileVisual(proj);
        this.visuals.set(proj.id, visual);
      }

      // The shapes are painted around (0, 0) and `position` is the hitbox's top-left: draw (and trail)
      // at the hitbox centre, where collision measures from (projectile-timing batch 1).
      const cx = proj.position.x + proj.width / 2, cy = proj.position.y + proj.height / 2;
      visual.graphics.setPosition(cx, cy);
      visual.drawX = cx;
      visual.drawY = cy;

      // Ribbon feed (trails spec §4.2): position OWNERS feed TrailSystem —
      // this loop already iterates every live projectile with fresh
      // positions, and projectiles are not resolvable entity ids in the
      // VFXEffectPlayer resolver. O(1) no-op for unattached projectiles.
      // (Timestamp deliberately defaulted — TrailSystem runs on Date.now();
      // this loop's `now` is performance.now(), a different clock.)
      TrailSystem.feed(proj.id, cx, cy);

      if (visual.throwingStar) {
        // Same launch age as physics, including a remotely replayed launch.
        visual.graphics.setRotation(throwingStarRotation(simulationNow - proj.createdAt));
      } else if (visual.spinSpeed) {
        // Custom auto-spin (spiral). Accumulate via dt rather than the
        // prior 1/60 approximation so spin speed is frame-rate independent.
        const cur = (visual.graphics as IGraphics & { _accumRotation?: number })._accumRotation ?? 0;
        const next = cur + visual.spinSpeed * dtSec;
        (visual.graphics as IGraphics & { _accumRotation?: number })._accumRotation = next;
        visual.graphics.setRotation(next);
      } else if (proj.velocity.x !== 0 || proj.velocity.y !== 0) {
        visual.graphics.setRotation(Math.atan2(proj.velocity.y, proj.velocity.x));
      }
    }

    // Remove orphaned visuals
    for (const [id, visual] of this.visuals) {
      if (!activeIds.has(id)) {
        this.releaseVisual(visual);
        this.visuals.delete(id);
        // Safety net: ProjectileSystem.removeProjectile is the canonical
        // release point, but an orphaned visual means the sim entry is gone
        // — make sure its ribbon drains too (idempotent).
        TrailSystem.release(id);
      }
    }
  }

  private releaseVisual(visual: ProjectileVisual): void {
    const g = visual.graphics;
    if (isParkable(g)) {
      (g as IGraphics & { _accumRotation?: number })._accumRotation = 0;
      this.pool.release(visual.poolKey, g as IGraphics & Parkable);
    } else {
      g.destroy();
    }
  }

  private createProjectileVisual(proj: Projectile): ProjectileVisual {
    const color = proj.color || 0xFFFF00;
    const shapeConfig = {
      width: proj.width,
      height: proj.height,
      strokeColor: proj.strokeColor,
      strokeWidth: proj.strokeWidth,
    };

    // Determine shape: prefer explicit shape field, fall back to type-based inference
    const shape = proj.shape
      || (proj.type === 'special' ? 'circle' : 'rect');

    const throwingStar = isThrowingStarAbility(proj.abilityId ?? '');
    const builderName = throwingStar ? 'throwing_star' : (SHAPE_BUILDERS[shape] ? shape : 'rect');
    const poolKey = [builderName, color, proj.width, proj.height, proj.strokeColor, proj.strokeWidth].join('|');
    let graphics: IGraphics | null = this.pool.take(poolKey);
    if (!graphics) {
      graphics = createGraphics(this.scene, { depth: Z_LAYERS.PROJECTILES });
      if (isParkable(graphics)) this.pool.noteMade();
      const builder = throwingStar ? buildThrowingStar : SHAPE_BUILDERS[builderName];
      builder(graphics, shapeConfig, color);
    }
    graphics.setPosition(proj.position.x + proj.width / 2, proj.position.y + proj.height / 2);

    return {
      graphics,
      poolKey,
      // Spiral swirls; Hammer of Wrath's hammer tumbles end over end.
      spinSpeed: SHAPE_SPIN[shape],
      throwingStar,
    };
  }

  public cleanup(): void {
    if (ProjectileRenderer.live === this) ProjectileRenderer.live = null;
    this.visuals.forEach(v => v.graphics.destroy());
    this.visuals.clear();
    this.pool.drain();
  }

  /** Pool counters for diagnostics + tests. */
  public getPoolStats() {
    return this.pool.stats();
  }
}
